import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Monitor, Smartphone, ShieldAlert, ShieldCheck, Clock } from "lucide-react";
import { ChangePassword } from "@/components/AccountSecurity";
import { useAuth } from "@/context/AuthContext";

const when = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "");
const isPhone = (d = "") => /iPhone|Android|iPad/.test(d);

const SignInRow = ({ s, onNotMe }) => {
  const Icon = isPhone(s.device) ? Smartphone : Monitor;
  return (
    <div data-testid={`signin-${s.sid}`} className={`flex flex-wrap sm:flex-nowrap items-start gap-x-4 gap-y-3 p-4 sm:p-5 rounded-2xl border transition-colors ${s.current ? "border-gold/40 bg-gold/5" : "border-white/8 bg-surface/60"}`}>
      <span className="w-11 h-11 rounded-full bg-white/5 flex items-center justify-center shrink-0"><Icon size={18} className="text-gold" /></span>
      <div className="flex-1 min-w-0 basis-[calc(100%-4rem)] sm:basis-0">
        <p className="text-sm flex flex-wrap items-center gap-2">
          {s.device}
          {s.current && <span className="text-[9px] uppercase tracking-[0.15em] text-gold border border-gold/40 rounded-full px-2 py-0.5">This device</span>}
          {s.new_device && !s.current && <span data-testid={`signin-new-${s.sid}`} className="text-[9px] uppercase tracking-[0.15em] text-amber-300 border border-amber-400/40 rounded-full px-2 py-0.5">New device</span>}
          {s.revoked && <span className="text-[9px] uppercase tracking-[0.15em] text-text-muted border border-white/15 rounded-full px-2 py-0.5">Signed out</span>}
        </p>
        <p className="text-xs text-text-muted mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
          <span className="flex items-center gap-1"><Clock size={11} /> {when(s.created_at)}</span>
          <span className="flex items-center gap-1"><MapPin size={11} /> {s.location || "Unknown location"}</span>
          <span className="font-mono text-[11px] text-text-muted/70">{s.ip}</span>
        </p>
      </div>
      {!s.current && !s.revoked && (
        <button data-testid={`signin-not-me-${s.sid}`} onClick={() => onNotMe(s)}
          className="ml-[3.75rem] sm:ml-0 shrink-0 text-[10px] uppercase tracking-[0.15em] px-4 py-2.5 rounded-full border border-red-400/40 text-red-300 hover:bg-red-500/10 transition-colors">
          Not me
        </button>
      )}
    </div>
  );
};

export default function SignIns() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [secure, setSecure] = useState(false);
  const load = () => api.get("/account/signins").then(({ data }) => setRows(data.signins)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const notMe = async (s) => {
    if (!window.confirm(`Sign out ${s.device} and every other device except this one?`)) return;
    try {
      const { data } = await api.post(`/account/signins/${s.sid}/not-me`);
      toast.success(`${data.revoked} device${data.revoked === 1 ? "" : "s"} signed out. Now change your password.`);
      setSecure(true); load();
      setTimeout(() => document.getElementById("secure-panel")?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't sign that device out"); }
  };

  return (
    <div data-testid="signins-page" className="pt-24 pb-16 max-w-3xl mx-auto px-4 sm:px-6">
      <Link to="/me" data-testid="signins-back" className="inline-flex items-center gap-2 text-sm text-text-muted hover:text-text-main mb-6"><ArrowLeft size={15} /> Profile</Link>
      <p className="text-[10px] uppercase tracking-[0.25em] text-gold flex items-center gap-2"><ShieldCheck size={13} /> Account & security</p>
      <h1 className="font-display text-4xl sm:text-5xl font-light mt-2">Sign-in history</h1>
      <p className="text-sm text-text-muted mt-3 mb-8">Your last 20 sign-ins. If you don't recognise one, tap <span className="text-red-300">Not me</span> to sign it out, then change your password.</p>
      {secure && (
        <div id="secure-panel" data-testid="secure-panel" className="mb-8 rounded-3xl border border-red-400/30 bg-red-500/5 p-5 sm:p-6 space-y-4">
          <p className="flex items-center gap-2 text-sm text-red-200"><ShieldAlert size={16} /> Other devices are signed out. Change your password to lock them out for good.</p>
          <ChangePassword hasPassword={!!user?.has_password} />
        </div>
      )}
      <div className="space-y-3">
        {rows === null && <p className="text-sm text-text-muted">Loading…</p>}
        {rows?.length === 0 && <p data-testid="signins-empty" className="text-sm text-text-muted">No sign-ins recorded yet.</p>}
        {rows?.map((s) => <SignInRow key={s.sid} s={s} onNotMe={notMe} />)}
      </div>
    </div>
  );
}
